import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import {
  FileSignature, Plus, Trash2, Search, Download, Check, X, Clock,
  ChevronUp, ChevronDown, ChevronsUpDown, Settings2, ChevronLeft, ChevronRight,
  Loader2, Mail, MessageCircle, Send, Eye, RefreshCw, Pencil, Brain, Info, Folder, Calendar, UserCircle, ArrowLeft,
} from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { useAppearance } from "../contexts/AppearanceContext";
import { usePermissions } from "../hooks/usePermissions";
import { applyMask } from "../components/PhoneInput";
import { JsonRenderer } from "../components/negocio-detail/NegocioDialogs";
import ContractDetailModal from "../components/ContractDetailModal";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 50;

const STATUS_MAP = {
  pending: { label: "Pendente", bg: "bg-amber-100 text-amber-700" },
  signed: { label: "Assinado", bg: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Recusado", bg: "bg-rose-100 text-rose-700" },
  revoked: { label: "Excluído", bg: "bg-slate-200 text-slate-600" },
  deleted: { label: "Excluído", bg: "bg-slate-200 text-slate-600" },
};

export default function ContratosPage() {
  const appearance = useAppearance();
  const { can } = usePermissions();
  const tc500 = appearance.colors?.["500"] || "#6366f1";
  const tc50 = appearance.colors?.["50"] || "#eef2ff";
  const phoneMask = appearance.phone_mask || "(##) #####-####";
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open wizard from ganho flow
  useEffect(() => {
    const isNew = searchParams.get("new");
    const negId = searchParams.get("negocio_id");
    const tplId = searchParams.get("template_id");
    const isGanho = searchParams.get("ganho");
    if (isNew && negId && tplId) {
      setSearchParams({});
      if (isGanho) setIsGanhoFlow(true);
      // Open wizard with pre-filled data, skip to step 2
      (async () => {
        try {
          const [tR, nR, cR, eR] = await Promise.all([
            axios.get(`${API}/autentique/templates`),
            axios.get(`${API}/negocios?limit=200&skip=0&status_filter=ganho&include_finalized=true&sort_by=ganho_em&sort_dir=desc`),
            axios.get(`${API}/contatos`).catch(() => ({ data: [] })),
            axios.get(`${API}/empresas`).catch(() => ({ data: [] })),
          ]);
          setTemplates(tR.data || []);
          setNegocios(nR.data?.data || nR.data || []);
          setContatos(Array.isArray(cR.data) ? cR.data : cR.data?.data || []);
          setEmpresas(Array.isArray(eR.data) ? eR.data : eR.data?.data || []);
          setNewForm({ template_id: tplId, negocio_id: negId });
          // Load template config
          const tplRes = await axios.get(`${API}/autentique/templates/${tplId}`);
          setTemplateConfig(tplRes.data);
          if (tplRes.data.signers?.length > 0) {
            setNewSigners(tplRes.data.signers.map(s => ({
              name: s.name || "", channel: s.delivery_method === "DELIVERY_METHOD_WHATSAPP" ? "whatsapp" : "email",
              email: s.email || "", phone: s.phone || "",
            })));
          }
          // Load last validation
          const nRes = await axios.get(`${API}/negocios/${negId}`, { withCredentials: true });
          const ct = (Array.isArray(cR.data) ? cR.data : cR.data?.data || []).find(c => c.id === nRes.data?.contato_id);
          if (ct) {
            let email = ct.email || "";
            let phone = "";
            for (const tc of ct.tipos_contato || []) {
              if (!email && ["e-mail", "email"].includes((tc.tipo || "").toLowerCase())) email = tc.valor;
              if (!phone && ["celular", "whatsapp", "telefone"].includes((tc.tipo || "").toLowerCase())) phone = (tc.valor || "").replace(/\D/g, "");
            }
            setNewSigners(prev => {
              const u = [...prev];
              if (u[0]) { u[0] = { ...u[0], name: u[0].name || ct.nome || "", email: email || u[0].email, phone: phone || u[0].phone }; }
              return u;
            });
          }
          const lv = nRes.data?.last_ai_validation;
          if (lv?.validated_at) {
            setWinAiResult({ approved: lv.approved, reason: lv.reason, data: lv.data || {}, validated_at: lv.validated_at, validated_by: lv.validated_by, _cached: true });
          }
          // Determine start step
          const hasVal = tplRes.data.required_documents?.length > 0 || tplRes.data.ai_validation_enabled;
          setNewStep(hasVal ? 1 : 2);
          setNewOpen(true);
        } catch { toast.error("Erro ao carregar dados"); }
      })();
    }
  }, []); // eslint-disable-line

  const [contracts, setContracts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [rowSelection, setRowSelection] = useState({});
  const [columnVisibility, setColumnVisibility] = useState({});

  // Dialogs
  const [selectedContract, setSelectedContract] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [bulkRevokeOpen, setBulkRevokeOpen] = useState(false);
  const [resendTarget, setResendTarget] = useState(null);
  const [resendHistory, setResendHistory] = useState([]);
  const [resendLoading, setResendLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newStep, setNewStep] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [negocios, setNegocios] = useState([]);
  const [stagesMap, setStagesMap] = useState({});
  const [contatos, setContatos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [newForm, setNewForm] = useState({ template_id: "", negocio_id: "" });
  const [newSigners, setNewSigners] = useState([{ name: "", channel: "email", email: "", phone: "" }]);
  const [sending, setSending] = useState(false);
  const [negocioPopoverOpen, setNegocioPopoverOpen] = useState(false);
  const [negocioSearchTerm, setNegocioSearchTerm] = useState("");
  // Step 2: Validation
  const [templateConfig, setTemplateConfig] = useState(null);
  const [winDocUploads, setWinDocUploads] = useState({});
  const [winAiResult, setWinAiResult] = useState(null);
  const [winAiValidating, setWinAiValidating] = useState(false);
  const [aiDetailOpen, setAiDetailOpen] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [filePickerOpen, setFilePickerOpen] = useState(false);
  const [filePickerIdx, setFilePickerIdx] = useState(null);
  const [filePickerList, setFilePickerList] = useState([]);
  const [filePickerLoading, setFilePickerLoading] = useState(false);

  useEffect(() => { const t = setTimeout(() => { setSearchDebounced(search); setPage(0); }, 350); return () => clearTimeout(t); }, [search]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: String(page * PAGE_SIZE), limit: String(PAGE_SIZE) });
      if (searchDebounced) params.set("search", searchDebounced);
      if (statusFilter) params.set("status", statusFilter);
      const r = await axios.get(`${API}/autentique/contracts?${params}`);
      setContracts(r.data.data || []);
      setTotal(r.data.total || 0);
    } catch { setContracts([]); setTotal(0); }
    setLoading(false);
  }, [page, searchDebounced, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setRowSelection({}); }, [contracts]);

  // Sync pending contracts with Autentique on page load
  useEffect(() => {
    axios.post(`${API}/autentique/sync-pending`).then(r => {
      if (r.data.synced > 0) fetchData();
    }).catch(() => {});
  }, []); // eslint-disable-line

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const selectedIds = Object.keys(rowSelection).filter(k => rowSelection[k]);
  const hasSelection = selectedIds.length > 0;

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
    setPage(0);
  };
  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ChevronsUpDown className="w-3.5 h-3.5 text-slate-300" />;
    return sortDir === "asc" ? <ChevronUp className="w-3.5 h-3.5 text-slate-700" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-700" />;
  };

  const columns = useMemo(() => [
    { id: "select", size: 40, enableSorting: false,
      header: ({ table }) => <Checkbox checked={table.getIsAllPageRowsSelected()} onCheckedChange={v => table.toggleAllPageRowsSelected(!!v)} />,
      cell: ({ row }) => <Checkbox checked={row.getIsSelected()} onCheckedChange={v => row.toggleSelected(!!v)} onClick={e => e.stopPropagation()} />,
    },
    { id: "negocio_titulo", header: "Negócio", size: 200, enableSorting: false,
      cell: ({ row }) => <span className="font-medium text-slate-900">{row.original.negocio_titulo || <span className="text-slate-300">-</span>}</span>,
    },
    { id: "doc_name", accessorKey: "doc_name", header: "Contrato", size: 240,
      cell: ({ row }) => {
        return <span className="text-sm text-slate-600 truncate">{row.original.doc_name}</span>;
      },
    },
    { id: "contato_nome", header: "Contato", size: 150, enableSorting: false,
      cell: ({ row }) => <span className="text-sm text-slate-600">{row.original.contato_nome || <span className="text-slate-300">-</span>}</span>,
    },
    { id: "empresa_nome", header: "Empresa", size: 150, enableSorting: false,
      cell: ({ row }) => <span className="text-sm text-slate-600">{row.original.empresa_nome || <span className="text-slate-300">-</span>}</span>,
    },
    { id: "status", header: "Status", size: 100, enableSorting: false,
      cell: ({ row }) => { const st = STATUS_MAP[row.original.status] || STATUS_MAP.pending; return <Badge className={`${st.bg} border-0 text-[10px]`}>{st.label}</Badge>; },
    },
    { id: "created_at", accessorKey: "created_at", header: "Criado em", size: 150,
      cell: ({ row }) => <span className="text-sm text-slate-500">{new Date(row.original.created_at).toLocaleDateString("pt-BR")} {new Date(row.original.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>,
    },
    { id: "actions", size: 80, enableSorting: false, header: "",
      cell: ({ row }) => (
        <div className="flex items-center gap-3 justify-end">
          <Tooltip><TooltipTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-sky-600" onClick={e => { e.stopPropagation(); setSelectedContract(row.original); }}>
              <Eye className="w-4 h-4" />
            </Button>
          </TooltipTrigger><TooltipContent>Detalhes</TooltipContent></Tooltip>
          {row.original.status !== "revoked" && (
            <Tooltip><TooltipTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-rose-500" onClick={e => { e.stopPropagation(); setRevokeTarget(row.original); }}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </TooltipTrigger><TooltipContent>Excluir</TooltipContent></Tooltip>
          )}
        </div>
      ),
    },
  ], []);

  const toggleableColumns = useMemo(() => columns.filter(c => c.id !== "select" && c.id !== "actions").map(c => ({ id: c.id, label: c.header || c.id })), [columns]);

  const table = useReactTable({
    data: contracts, columns, getCoreRowModel: getCoreRowModel(),
    manualSorting: true, manualPagination: true, pageCount: totalPages,
    state: { rowSelection, columnVisibility },
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    enableRowSelection: true,
    getRowId: row => row.id,
  });

  // New contract
  const openNew = async () => {
    setNewStep(0);
    setNewForm({ template_id: "", negocio_id: "" });
    setNewSigners([{ name: "", channel: "email", email: "", phone: "" }]);
    setTemplateConfig(null);
    setWinDocUploads({});
    setWinAiResult(null);
    try {
      const [tR, nR, cR, eR] = await Promise.all([
        axios.get(`${API}/autentique/templates`),
        axios.get(`${API}/negocios?limit=200&skip=0&status_filter=ganho&include_finalized=true&sort_by=ganho_em&sort_dir=desc`),
        axios.get(`${API}/contatos`).catch(() => ({ data: [] })),
        axios.get(`${API}/empresas`).catch(() => ({ data: [] })),
      ]);
      setTemplates(tR.data || []);
      const negList = nR.data?.data || nR.data || [];
      setNegocios(negList);
      // Enrich with stage names
      const sIds = [...new Set(negList.map(n => n.estagio_id).filter(Boolean))];
      if (sIds.length > 0) {
        const pIds = [...new Set(negList.map(n => n.pipeline_id).filter(Boolean))];
        for (const pid of pIds) {
          axios.get(`${API}/pipeline-stages?pipeline_id=${pid}`).then(r => {
            const map = {};
            (r.data || []).forEach(s => { map[s.id] = s.name; });
            setStagesMap(prev => ({ ...prev, ...map }));
          }).catch(() => {});
        }
      }
      setContatos(Array.isArray(cR.data) ? cR.data : cR.data?.data || []);
      setEmpresas(Array.isArray(eR.data) ? eR.data : eR.data?.data || []);
    } catch {}
    // Get Autentique account owner email
    try { const aRes = await axios.get(`${API}/autentique/account-info`); setOwnerEmail(aRes.data?.email || ""); } catch {}
    setNewOpen(true);
  };

  const onNegocioSelect = (negId) => {
    if (negId === "none") { setNewForm(f => ({ ...f, negocio_id: "" })); return; }
    const neg = negocios.find(n => n.id === negId);
    if (!neg) { setNewForm(f => ({ ...f, negocio_id: negId })); return; }
    const ct = contatos.find(c => c.id === neg.contato_id);
    const emp = empresas.find(e => e.id === neg.empresa_id);
    let email = ct?.email || "";
    let phone = "";
    if (ct?.tipos_contato) {
      for (const tc of ct.tipos_contato) {
        const tipo = (tc.tipo || "").toLowerCase();
        if (!email && ["e-mail", "email"].includes(tipo) && tc.valor) email = tc.valor;
        if (!phone && ["celular", "whatsapp", "telefone"].includes(tipo) && tc.valor) phone = tc.valor.replace(/\D/g, "");
      }
    }
    setNewForm(f => ({ ...f, negocio_id: negId }));
    // Pre-fill first signer from contact
    if (email || phone) {
      setNewSigners(prev => {
        const updated = [...prev];
        updated[0] = { ...updated[0], name: ct?.nome || "", email: email || updated[0].email, phone: phone || updated[0].phone, channel: email ? "email" : "whatsapp" };
        return updated;
      });
    }
  };

  // Fetch win flow config when negocio is selected
  // Load template config when template is selected
  const loadTemplateConfig = async (templateId) => {
    setTemplateConfig(null);
    setWinDocUploads({});
    setWinAiResult(null);
    if (!templateId) return;
    try {
      const r = await axios.get(`${API}/autentique/templates/${templateId}`);
      setTemplateConfig(r.data);
      // Pre-fill signers from template
      if (r.data.signers?.length > 0) {
        setNewSigners(r.data.signers.map(s => ({
          name: s.name || "",
          channel: s.delivery_method === "DELIVERY_METHOD_WHATSAPP" ? "whatsapp" : "email",
          email: s.email || "",
          phone: s.phone || "",
        })));
      }
    } catch { setTemplateConfig(null); }
  };

  // Load last AI validation when negocio is selected
  const loadLastValidation = async (negId) => {
    if (!negId) return;
    try {
      const nRes = await axios.get(`${API}/negocios/${negId}`, { withCredentials: true });
      const lastValidation = nRes.data?.last_ai_validation;
      if (lastValidation && lastValidation.validated_at) {
        setWinAiResult({
          approved: lastValidation.approved,
          reason: lastValidation.reason,
          data: lastValidation.data || {},
          validated_at: lastValidation.validated_at,
          validated_by: lastValidation.validated_by,
          _cached: true,
        });
        if (lastValidation.file_ids?.length) {
          const filesRes = await axios.get(`${API}/files/list?negocio_id=${negId}`);
          const allFiles = filesRes.data || [];
          const uploads = {};
          lastValidation.file_ids.forEach((fid, i) => {
            const f = allFiles.find(af => af.id === fid);
            if (f) uploads[i] = { ...f, _existing: true, name: f.original_filename };
          });
          setWinDocUploads(uploads);
        }
      }
    } catch {}
  };

  const validateWithAI = async () => {
    if (!newForm.negocio_id) return;
    setWinAiValidating(true);
    setWinAiResult(null);
    try {
      const docIds = [];
      for (const [idx, item] of Object.entries(winDocUploads)) {
        if (item.id) {
          docIds.push(item.id);
        }
      }
      const r = await axios.post(`${API}/negocios/${newForm.negocio_id}/validate-win`, {
        file_ids: docIds,
        prompt: templateConfig?.ai_prompt || "",
      });
      if (!r.data.approved) {
        // Keep files in the deal (don't delete), just clear selection for retry
        setWinDocUploads({});
      }
      setWinAiResult({ approved: r.data.approved, reason: r.data.reason || "", data: r.data.data || {}, validated_at: new Date().toISOString() });
    } catch (err) {
      setWinDocUploads({});
      setWinAiResult({ approved: false, reason: err.response?.data?.detail || "Erro na validação", data: {} });
    }
    setWinAiValidating(false);
  };

  const hasValidation = templateConfig && (templateConfig.required_documents?.length > 0 || templateConfig.ai_validation_enabled);

  const [isGanhoFlow, setIsGanhoFlow] = useState(false);

  const sendContract = async () => {
    const validSigners = newSigners.filter(s => s.name && (s.channel === "email" ? s.email : s.phone));
    if (validSigners.length === 0) { toast.error("Adicione pelo menos um signatário com nome e contato."); return; }
    setSending(true);
    try {
      await axios.post(`${API}/autentique/contracts/send`, {
        ...newForm,
        signers: validSigners,
        ai_validation_data: winAiResult?.data || {},
      });
      // If this came from ganho flow, execute the ganho now
      if (isGanhoFlow && newForm.negocio_id) {
        await axios.patch(`${API}/negocios/${newForm.negocio_id}/ganhar`, {
          ai_validation_data: winAiResult?.data || {},
        }, { withCredentials: true });
        toast.success("Negócio ganho e contrato enviado!");
      } else {
        toast.success("Contrato enviado!");
      }
      setNewOpen(false);
      setIsGanhoFlow(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao enviar"); }
    setSending(false);
  };

  const handleExport = async () => {
    try {
      const XLSX = await import("xlsx");
      const allRes = await axios.get(`${API}/autentique/contracts?skip=0&limit=9999`);
      const all = allRes.data?.data || allRes.data || [];
      const rows = all.map(c => ({
        "Contrato": c.doc_name, "Negócio": c.negocio_titulo || "", "Contato": c.contato_nome || "",
        "Empresa": c.empresa_nome || "", "Status": STATUS_MAP[c.status]?.label || c.status,
        "Data": new Date(c.created_at).toLocaleDateString("pt-BR"), "Enviado por": c.created_by_name || "",
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = Object.keys(rows[0] || {}).map(k => ({ wch: Math.max(k.length + 2, 15) }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Contratos");
      XLSX.writeFile(wb, `contratos_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch { toast.error("Erro ao exportar"); }
  };

  return (
    <div data-testid="contratos-page" className="space-y-4">
      {/* Header — identical to ContatosPage */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}>
            <FileSignature className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2">
              Contratos
              <Badge className="bg-white text-slate-500 border border-slate-300 text-sm font-medium px-3 py-1">{total}</Badge>
              {hasSelection && (
                <Tooltip><TooltipTrigger asChild>
                  <Badge className="text-white text-sm font-medium px-3 py-1 cursor-default" style={{ backgroundColor: tc500 }}>{selectedIds.length}</Badge>
                </TooltipTrigger><TooltipContent>{selectedIds.length} contrato(s) selecionado(s)</TooltipContent></Tooltip>
              )}
            </h1>
            <p className="text-slate-500 mt-0.5">Gerencie os contratos enviados para assinatura digital.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hasSelection && (<>
            <Tooltip><TooltipTrigger asChild>
              <Button size="icon" className="h-9 w-9 text-white bg-rose-500 hover:bg-rose-600" onClick={() => setBulkRevokeOpen(true)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </TooltipTrigger><TooltipContent>Excluir selecionados</TooltipContent></Tooltip>
            <Tooltip><TooltipTrigger asChild>
              <Button variant="outline" size="icon" className="h-9 w-9 text-slate-600" onClick={() => setRowSelection({})}>
                <X className="w-4 h-4" />
              </Button>
            </TooltipTrigger><TooltipContent>Limpar seleção</TooltipContent></Tooltip>
            <div className="w-px h-6 bg-slate-200" />
          </>)}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar contrato..." className="pl-9 w-full sm:w-56 h-9 text-sm" />
          </div>
          <Select value={statusFilter || "all"} onValueChange={v => { setStatusFilter(v === "all" ? "" : v); setPage(0); }}>
            <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pending">Pendente</SelectItem>
              <SelectItem value="signed">Assinado</SelectItem>
              <SelectItem value="rejected">Recusado</SelectItem>
              <SelectItem value="revoked">Revogado</SelectItem>
            </SelectContent>
          </Select>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon" className="h-9 w-9 text-slate-600"><Settings2 className="w-4 h-4" /></Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-2 mb-2">Colunas visíveis</p>
              {toggleableColumns.map(col => {
                const isVisible = table.getColumn(col.id)?.getIsVisible() ?? true;
                return (
                  <label key={col.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm text-slate-700">
                    <Checkbox checked={isVisible} onCheckedChange={v => table.getColumn(col.id)?.toggleVisibility(!!v)} />
                    {col.label}
                  </label>
                );
              })}
            </PopoverContent>
          </Popover>
          <Tooltip><TooltipTrigger asChild>
            <Button variant="outline" size="icon" className="h-9 w-9 text-slate-600" onClick={handleExport}><Download className="w-4 h-4" /></Button>
          </TooltipTrigger><TooltipContent>Exportar</TooltipContent></Tooltip>
          <Tooltip><TooltipTrigger asChild>
            <Button onClick={openNew} size="icon" style={{ backgroundColor: tc500 }} className="text-white h-9 w-9 hover:opacity-90">
              <Plus className="w-4 h-4" />
            </Button>
          </TooltipTrigger><TooltipContent>Novo contrato</TooltipContent></Tooltip>
        </div>
      </div>

      {/* Table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              {table.getHeaderGroups().map(hg => (
                <tr key={hg.id} className="border-b border-slate-200 bg-slate-50/80">
                  {hg.headers.map(header => {
                    const canSort = header.column.columnDef.enableSorting !== false && header.column.columnDef.accessorKey;
                    return (
                      <th key={header.id} style={{ width: header.getSize() }}
                        className={`px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap ${canSort ? "cursor-pointer select-none hover:text-slate-700" : ""}`}
                        onClick={() => canSort && handleSort(header.column.columnDef.accessorKey)}>
                        <div className="flex items-center gap-1.5">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort && <SortIcon col={header.column.columnDef.accessorKey} />}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                [...Array(8)].map((_, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    {columns.filter(c => table.getColumn(c.id)?.getIsVisible() !== false).map((c, j) => (
                      <td key={j} className="px-3 py-3"><div className="h-4 bg-slate-100 rounded animate-pulse" style={{ width: `${40 + Math.random() * 40}%` }} /></td>
                    ))}
                  </tr>
                ))
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-16">
                    <div className="flex flex-col items-center text-slate-400">
                      <FileSignature className="w-10 h-10 mb-3 text-slate-300" />
                      <p className="text-sm">Nenhum contrato encontrado.</p>
                    </div>
                  </td>
                </tr>
              ) : table.getRowModel().rows.map(row => (
                <tr key={row.id}
                  className={`border-b border-slate-100 transition-colors cursor-pointer ${row.getIsSelected() ? "bg-slate-50" : "hover:bg-slate-50/50"}`}
                  onClick={() => setSelectedContract(row.original)}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="px-3 py-2.5">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">{total} contrato(s) · Página {page + 1} de {totalPages}</p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page === 0} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            {[...Array(Math.min(totalPages, 5))].map((_, i) => {
              const pg = page < 3 ? i : page - 2 + i;
              if (pg >= totalPages) return null;
              return <Button key={pg} variant={pg === page ? "default" : "outline"} size="sm" className="h-8 w-8 p-0 text-xs" style={pg === page ? { backgroundColor: tc500 } : {}} onClick={() => setPage(pg)}>{pg + 1}</Button>;
            })}
            <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}

      {/* Detail Modal — same design as Resumo step */}
      <ContractDetailModal
        contract={selectedContract}
        open={!!selectedContract}
        onClose={() => setSelectedContract(null)}
        onRefresh={fetchData}
      />

      {/* Delete Confirm */}
      <AlertDialog open={!!revokeTarget} onOpenChange={v => { if (!v) setRevokeTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-600"><Trash2 className="w-5 h-5" />Excluir contrato?</AlertDialogTitle>
            <AlertDialogDescription>A assinatura será bloqueada e o documento será excluído da Autentique. Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700 text-white" onClick={async () => {
              if (!revokeTarget) return;
              try { await axios.delete(`${API}/autentique/contracts/${revokeTarget.id}`); fetchData(); setSelectedContract(null); toast.success("Contrato excluído!"); } catch { toast.error("Erro"); }
              setRevokeTarget(null);
            }}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete */}
      <AlertDialog open={bulkRevokeOpen} onOpenChange={setBulkRevokeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-rose-600">Excluir {selectedIds.length} contrato(s)?</AlertDialogTitle>
            <AlertDialogDescription>Todos os contratos selecionados terão a assinatura bloqueada e serão excluídos da Autentique.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700 text-white" onClick={async () => {
              for (const id of selectedIds) { try { await axios.delete(`${API}/autentique/contracts/${id}`); } catch {} }
              setRowSelection({}); fetchData(); toast.success("Contratos excluídos!");
              setBulkRevokeOpen(false);
            }}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Resend Confirmation Modal with History */}
      <Dialog open={!!resendTarget} onOpenChange={v => { if (!v) setResendTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-sm"><Send className="w-4 h-4" style={{ color: tc500 }} />Reenviar contrato</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {resendLoading ? (
              <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin" style={{ color: tc500 }} /></div>
            ) : (
              <>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Histórico de envios</p>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {resendHistory.map((h, i) => (
                    <div key={i} className="flex items-center gap-3 p-2.5 bg-white border border-slate-200 rounded-lg">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${h.is_original ? "bg-indigo-50" : "bg-slate-100"}`}>
                        <Send className={`w-3 h-3 ${h.is_original ? "text-indigo-500" : "text-slate-400"}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-700">
                          {h.is_original ? "Envio original" : "Reenvio"}
                        </p>
                        <p className="text-[10px] text-slate-400">
                          {new Date(h.sent_at).toLocaleDateString("pt-BR")} às {new Date(h.sent_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} · {h.sent_by}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500">Deseja realmente reenviar este contrato para assinatura?</p>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setResendTarget(null)}>Cancelar</Button>
                  <Button className="flex-1 text-white" style={{ backgroundColor: tc500 }} disabled={resending} onClick={async () => {
                    setResending(true);
                    try {
                      await axios.post(`${API}/autentique/contracts/${resendTarget.id}/resend`);
                      toast.success("Contrato reenviado!");
                      setResendTarget(null);
                      setSelectedContract(null);
                      fetchData();
                    } catch { toast.error("Erro ao reenviar"); }
                    setResending(false);
                  }}>
                    {resending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Reenviar
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* New Contract Wizard — 4 Steps */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-lg !p-0 overflow-hidden" style={{ minHeight: "520px" }}>
          <div className="px-6 pt-5 pb-3">
            <p className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2 mb-5"><FileSignature className="w-5 h-5" style={{ color: tc500 }} />Novo Contrato</p>
            <div className="flex items-center w-full px-8">
              {[{ n: 1, label: "Contrato" }, { n: 2, label: "Validação" }, { n: 3, label: "Signatários" }, { n: 4, label: "Resumo" }].map((step, idx) => (
                <div key={step.n} className="flex items-center flex-1 last:flex-none">
                  <div className="flex flex-col items-center" style={{ width: "60px" }}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-colors ${newStep >= idx ? "text-white" : "bg-slate-100 text-slate-400"}`}
                      style={newStep >= idx ? { backgroundColor: tc500 } : undefined}>
                      {newStep > idx ? <Check className="w-4 h-4" /> : step.n}
                    </div>
                    <span className={`text-[10px] font-medium mt-1 ${newStep >= idx ? "text-slate-800" : "text-slate-400"}`}>{step.label}</span>
                  </div>
                  {idx < 3 && <div className={`h-px flex-1 -mt-4 ${newStep > idx ? "" : "bg-slate-200"}`} style={newStep > idx ? { backgroundColor: tc500 } : undefined} />}
                </div>
              ))}
            </div>
          </div>
          <div className="px-6 pb-6 flex flex-col" style={{ minHeight: "340px" }}>

            {/* Step 1: Contrato + Negócio */}
            {newStep === 0 && (
              <div className="space-y-4 flex-1 flex flex-col">
                <div className="flex-1 space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Modelo de contrato</Label>
                    <Select value={newForm.template_id} onValueChange={v => { setNewForm(f => ({ ...f, template_id: v })); loadTemplateConfig(v); }}>
                      <SelectTrigger className="mt-1.5"><SelectValue placeholder="Selecione o modelo..." /></SelectTrigger>
                      <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <Label className="text-sm font-medium text-slate-700">Negócio</Label>
                      <Tooltip><TooltipTrigger asChild>
                        <button className="text-slate-400 hover:text-slate-600"><Info className="w-3.5 h-3.5" /></button>
                      </TooltipTrigger><TooltipContent className="max-w-xs text-xs">Listados exclusivamente os negócios ganhos, na ordem decrescente de fechamento.</TooltipContent></Tooltip>
                    </div>
                    <Popover open={negocioPopoverOpen} onOpenChange={setNegocioPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full mt-1.5 justify-between font-normal bg-white text-slate-700 hover:bg-white hover:text-slate-700">
                          {newForm.negocio_id ? negocios.find(n => n.id === newForm.negocio_id)?.titulo || "Selecione..." : "Selecione o negócio..."}
                          <ChevronDown className="w-4 h-4 text-slate-400 ml-2" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <div className="p-2 border-b border-slate-200">
                          <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                            <Input value={negocioSearchTerm} onChange={e => setNegocioSearchTerm(e.target.value)} placeholder="Buscar negócio..." className="pl-8 h-8 text-sm" />
                          </div>
                        </div>
                        <div className="max-h-52 overflow-y-auto p-1" style={{ overscrollBehavior: "contain" }} onWheel={e => e.stopPropagation()}>
                          {negocios.filter(n => !negocioSearchTerm || n.titulo?.toLowerCase().includes(negocioSearchTerm.toLowerCase())).map(n => {
                            const responsavel = n.responsavel_nome || n.user_name || "";
                            return (
                              <button key={n.id} onClick={() => { onNegocioSelect(n.id); loadLastValidation(n.id); setNegocioPopoverOpen(false); setNegocioSearchTerm(""); }}
                                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left transition-colors ${newForm.negocio_id === n.id ? "font-medium" : "text-slate-700"} hover:bg-slate-100`}>
                                <Check className={`w-3.5 h-3.5 flex-shrink-0 ${newForm.negocio_id === n.id ? "opacity-100" : "opacity-0"}`} style={{ color: tc500 }} />
                                <span className="flex-1 truncate">{n.titulo}</span>
                                {responsavel && <span className="text-[10px] text-slate-400 flex-shrink-0 truncate max-w-[100px]">{responsavel}</span>}
                              </button>);
                          })}
                          {negocios.filter(n => !negocioSearchTerm || n.titulo?.toLowerCase().includes(negocioSearchTerm.toLowerCase())).length === 0 && (
                            <p className="text-xs text-slate-400 text-center py-3">Nenhum negócio encontrado.</p>)}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  {/* Info cards when negocio selected */}
                  {newForm.negocio_id && (() => {
                    const neg = negocios.find(n => n.id === newForm.negocio_id);
                    if (!neg) return null;
                    return (
                      <div className="flex gap-2">
                        <div className="flex-1 flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg">
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: tc50 }}>
                            <Calendar className="w-4 h-4" style={{ color: tc500 }} />
                          </div>
                          <div>
                            <p className="text-[10px] text-slate-400">Etapa</p>
                            <p className="text-sm font-semibold text-slate-800">{neg.status === "ganho" ? "Ganho" : stagesMap[neg.estagio_id] || "—"}</p>
                          </div>
                        </div>
                        <div className="flex-1 flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg">
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: tc50 }}>
                            <UserCircle className="w-4 h-4" style={{ color: tc500 }} />
                          </div>
                          <div>
                            <p className="text-[10px] text-slate-400">Executivo</p>
                            <p className="text-sm font-semibold text-slate-800">{neg.responsavel_nome || neg.user_name || "—"}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <Button className="w-full text-white" style={{ backgroundColor: tc500 }} disabled={!newForm.template_id || !newForm.negocio_id}
                  onClick={() => setNewStep(hasValidation ? 1 : 2)}>Avançar</Button>
              </div>
            )}

            {/* Step 2: Validação (docs + IA) */}
            {newStep === 1 && (
              <div className="space-y-4 flex-1 flex flex-col">
                <div className="flex-1 space-y-4">
                  {/* Required Documents */}
                  {templateConfig?.required_documents?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-700">Documentos obrigatórios</p>
                      {templateConfig.required_documents.map((doc, i) => {
                        const selected = winDocUploads[i];
                        return (
                          <div key={i} className="flex items-center gap-2 p-2.5 border border-slate-200 rounded-lg">
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-slate-800">{doc.name} <span className="text-rose-500">*</span></p>
                              {selected ? (
                                <p className="text-[10px] text-emerald-600 flex items-center gap-1 mt-0.5">
                                  <Check className="w-2.5 h-2.5" />{selected._existing ? selected.original_filename : selected.name}
                                  <button onClick={() => setWinDocUploads(prev => { const n = {...prev}; delete n[i]; return n; })} className="ml-1 text-slate-400 hover:text-rose-500"><X className="w-2.5 h-2.5" /></button>
                                </p>
                              ) : (
                                <p className="text-[10px] text-slate-400 mt-0.5">Nenhum arquivo</p>
                              )}
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <label className="cursor-pointer px-2 py-1 text-[10px] font-medium border border-slate-300 rounded hover:bg-slate-50">
                                Upload
                                <input type="file" className="hidden" onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  // Upload immediately to attach to deal
                                  try {
                                    const fd = new FormData();
                                    fd.append("file", file);
                                    fd.append("negocio_id", newForm.negocio_id);
                                    fd.append("doc_type", templateConfig?.required_documents?.[i]?.name || "");
                                    const r = await axios.post(`${API}/files/upload`, fd);
                                    setWinDocUploads(prev => ({ ...prev, [i]: { ...r.data, _existing: true, name: file.name, original_filename: file.name } }));
                                    toast.success(`${file.name} enviado!`);
                                  } catch { toast.error("Erro ao enviar arquivo"); }
                                }} />
                              </label>
                              <button onClick={async () => {
                                setFilePickerIdx(i);
                                setFilePickerLoading(true);
                                setFilePickerOpen(true);
                                try {
                                  const r = await axios.get(`${API}/files/list?negocio_id=${newForm.negocio_id}`);
                                  setFilePickerList(r.data || []);
                                } catch { setFilePickerList([]); }
                                setFilePickerLoading(false);
                              }} className="px-2 py-1 text-[10px] font-medium border border-slate-300 rounded hover:bg-slate-50">
                                Selecionar
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* AI Validation */}
                  {templateConfig?.ai_validation_enabled && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" className="text-xs gap-1.5" disabled={winAiValidating
                          || (templateConfig?.required_documents?.length > 0 && Object.keys(winDocUploads).length < templateConfig.required_documents.length)}
                          onClick={validateWithAI}>
                          {winAiValidating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Brain className="w-3 h-3" />}
                          Validar com IA
                        </Button>
                        <Tooltip><TooltipTrigger asChild>
                          <button className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"><Info className="w-4 h-4" /></button>
                        </TooltipTrigger><TooltipContent side="right" className="max-w-xs text-xs whitespace-pre-wrap">{templateConfig?.ai_prompt || "Sem prompt"}</TooltipContent></Tooltip>
                      </div>
                      {winAiResult && !winAiValidating && (
                        <div className={`p-3 rounded-lg text-sm ${winAiResult.approved ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-rose-50 border border-rose-200 text-rose-800"}`}>
                          <div className="flex items-center justify-between">
                            <p className="font-semibold flex items-center gap-2">
                              {winAiResult.approved ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                              {winAiResult.approved ? "Aprovado pela IA" : "Reprovado pela IA"}
                            </p>
                            <Tooltip><TooltipTrigger asChild>
                              <button onClick={() => setAiDetailOpen(true)} className={`w-6 h-6 rounded flex items-center justify-center transition-colors ${winAiResult.approved ? "hover:bg-emerald-100 text-emerald-600" : "hover:bg-rose-100 text-rose-600"}`}>
                                <Eye className="w-3.5 h-3.5" />
                              </button>
                            </TooltipTrigger><TooltipContent>Ver detalhes</TooltipContent></Tooltip>
                          </div>
                          {(winAiResult.reason || winAiResult.data?.resumo) && <p className="text-xs mt-2">{winAiResult.reason || winAiResult.data?.resumo}</p>}
                          {winAiResult.validated_at && (
                            <p className="text-[10px] mt-3 text-right opacity-60 italic">
                              {winAiResult._cached && "Última análise · "}
                              {new Date(winAiResult.validated_at).toLocaleDateString("pt-BR")} às {new Date(winAiResult.validated_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                              {winAiResult.validated_by && ` · ${winAiResult.validated_by}`}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {!templateConfig?.required_documents?.length && !templateConfig?.ai_validation_enabled && (
                    <div className="text-center py-6 text-slate-400">
                      <Folder className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm">Nenhuma validação configurada para esta pipeline.</p>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <button onClick={() => setNewStep(0)} className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"><ArrowLeft className="w-4 h-4" /></button>
                  <div className="flex-1" />
                  <Button className="text-white" style={{ backgroundColor: tc500 }}
                    disabled={templateConfig?.ai_validation_enabled && (!winAiResult || !winAiResult.approved)}
                    onClick={() => setNewStep(2)}>Avançar</Button>
                </div>
              </div>
            )}

            {/* Step 3: Signatários */}
            {newStep === 2 && (
              <div className="space-y-3 flex-1 flex flex-col">
                <div className="flex-1 space-y-3">
                  {newSigners.map((s, i) => {
                    const ne = s._touched && !s.name;
                    const ce = s._touched && (s.channel === "email" ? !s.email : !s.phone);
                    return (
                    <div key={i} className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Input value={s.name} onChange={e => { const u = [...newSigners]; u[i] = { ...u[i], name: e.target.value, _touched: true }; setNewSigners(u); }}
                          onBlur={() => { const u = [...newSigners]; u[i] = { ...u[i], _touched: true }; setNewSigners(u); }}
                          placeholder="Nome completo" className={`flex-1 h-9 text-sm ${ne ? "border-rose-300" : ""}`} />
                        <button onClick={() => { const u = [...newSigners]; u[i] = { ...u[i], channel: s.channel === "email" ? "whatsapp" : "email" }; setNewSigners(u); }}
                          className={`h-9 px-2.5 rounded-md border flex items-center gap-1.5 text-xs font-medium flex-shrink-0 transition-colors ${s.channel === "email" ? "border-blue-300 bg-blue-50 text-blue-700" : "border-emerald-300 bg-emerald-50 text-emerald-700"}`}>
                          {s.channel === "email" ? <Mail className="w-3.5 h-3.5" /> : <MessageCircle className="w-3.5 h-3.5" />}
                          {s.channel === "email" ? "E-mail" : "WhatsApp"}
                        </button>
                        {newSigners.length > 1 && (
                          <button onClick={() => setNewSigners(prev => prev.filter((_, j) => j !== i))} className="h-9 w-9 flex items-center justify-center rounded-md text-slate-300 hover:text-rose-500 flex-shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>)}
                      </div>
                      {s.channel === "email" ? (
                        <Input value={s.email} onChange={e => { const u = [...newSigners]; u[i] = { ...u[i], email: e.target.value, _touched: true }; setNewSigners(u); }}
                          onBlur={() => { const u = [...newSigners]; u[i] = { ...u[i], _touched: true }; setNewSigners(u); }}
                          placeholder="email@empresa.com" className={`h-9 text-sm ${ce ? "border-rose-300" : ""}`} />
                      ) : (
                        <Input value={s.phone ? applyMask(s.phone.replace(/\D/g, "").replace(/^55/, ""), phoneMask) : ""}
                          onChange={e => { const u = [...newSigners]; u[i] = { ...u[i], phone: e.target.value.replace(/\D/g, ""), _touched: true }; setNewSigners(u); }}
                          onBlur={() => { const u = [...newSigners]; u[i] = { ...u[i], _touched: true }; setNewSigners(u); }}
                          placeholder={applyMask("11999999999", phoneMask)} className={`h-9 text-sm ${ce ? "border-rose-300" : ""}`} />
                      )}
                      {i < newSigners.length - 1 && <div className="border-b border-slate-100 my-1" />}
                    </div>);
                  })}
                  <button onClick={() => setNewSigners(prev => [...prev, { name: "", channel: "email", email: "", phone: "" }])}
                    className="w-full py-2 border border-dashed border-slate-300 rounded-lg text-xs text-slate-400 hover:text-slate-600 hover:border-slate-400 transition-colors flex items-center justify-center gap-1">
                    <Plus className="w-3 h-3" />Adicionar signatário
                  </button>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <button onClick={() => setNewStep(hasValidation ? 1 : 0)} className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"><ArrowLeft className="w-4 h-4" /></button>
                  <div className="flex-1" />
                  <Button className="text-white" style={{ backgroundColor: tc500 }}
                    disabled={!newSigners.some(s => s.name && (s.channel === "email" ? s.email : s.phone))}
                    onClick={() => {
                      if (newSigners.some(s => !s.name || (s.channel === "email" ? !s.email : !s.phone))) {
                        setNewSigners(prev => prev.map(s => ({ ...s, _touched: true })));
                        toast.error("Preencha nome e contato de todos os signatários.");
                        return;
                      }
                      setNewStep(3);
                    }}>Avançar</Button>
                </div>
              </div>
            )}

            {/* Step 4: Resumo */}
            {newStep === 3 && (() => {
              const neg = negocios.find(n => n.id === newForm.negocio_id);
              const ct = contatos.find(c => c.id === neg?.contato_id);
              const emp = empresas.find(e => e.id === neg?.empresa_id);
              const contatoNome = ct?.nome || neg?.contato_nome || "";
              const empresaNome = emp?.nome || neg?.empresa_nome || "";
              return (
              <div className="space-y-4 flex-1 flex flex-col">
                <div className="flex-1 space-y-3">
                  {/* Deal card */}
                  <div className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: tc50 }}>
                      <FileSignature className="w-4 h-4" style={{ color: tc500 }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800">{neg?.titulo || "—"}</p>
                      <p className="text-[11px] text-slate-400">{contatoNome}{contatoNome && empresaNome ? " · " : ""}{empresaNome}</p>
                    </div>
                  </div>

                  {/* AI validation badge */}
                  {winAiResult?.approved && (
                    <div className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <Check className="w-4 h-4 text-emerald-600" />
                      <span className="text-xs font-medium text-emerald-700">Documentos validados pela IA</span>
                    </div>
                  )}

                  {/* Signers */}
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Signatários</p>
                    <div className="space-y-1.5">
                      {/* Account owner — first */}
                      <div className="flex items-center gap-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-amber-100">
                          <FileSignature className="w-3.5 h-3.5 text-amber-600" />
                        </div>
                        <p className="text-[11px] text-amber-700 flex-1">O dono da conta{ownerEmail ? ` (${ownerEmail})` : ""} será marcado como signatário automaticamente.</p>
                      </div>
                      {newSigners.filter(s => s.name).map((s, i) => (
                        <div key={i} className="flex items-center gap-3 p-2.5 bg-white border border-slate-200 rounded-lg">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${s.channel === "email" ? "bg-blue-50" : "bg-emerald-50"}`}>
                            {s.channel === "email" ? <Mail className="w-3.5 h-3.5 text-blue-500" /> : <MessageCircle className="w-3.5 h-3.5 text-emerald-500" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800">{s.name}</p>
                            <p className="text-[11px] text-slate-400">{s.channel === "email" ? s.email : applyMask((s.phone || "").replace(/^55/, ""), phoneMask)}</p>
                          </div>
                          <Badge className={`border-0 text-[9px] ${s.channel === "email" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>
                            {s.channel === "email" ? "E-mail" : "WhatsApp"}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <button onClick={() => setNewStep(2)} className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"><ArrowLeft className="w-4 h-4" /></button>
                  <div className="flex-1" />
                  <Button variant="outline" className="gap-1.5" onClick={async () => {
                    try {
                      const r = await axios.post(`${API}/autentique/preview-contract`, { template_id: newForm.template_id, negocio_id: newForm.negocio_id, ai_validation_data: winAiResult?.data || {} });
                      if (r.data.html) {
                        const w = window.open("", "_blank"); w.document.write(r.data.html); w.document.close();
                      } else { toast.error("Preview não disponível"); }
                    } catch { toast.error("Erro ao gerar preview"); }
                  }}><Eye className="w-4 h-4" />Visualizar</Button>
                  <Button className="text-white" style={{ backgroundColor: tc500 }} disabled={sending} onClick={sendContract}>
                    {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Enviar contrato
                  </Button>
                </div>
              </div>);
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Detail Modal */}
      <Dialog open={aiDetailOpen} onOpenChange={setAiDetailOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm"><Brain className="w-4 h-4" style={{ color: tc500 }} />Detalhes da Validação IA</DialogTitle>
          </DialogHeader>
          {winAiResult?.data && <JsonRenderer data={winAiResult.data} />}
        </DialogContent>
      </Dialog>

      {/* File Picker Modal */}
      <Dialog open={filePickerOpen} onOpenChange={setFilePickerOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm"><Folder className="w-4 h-4" style={{ color: tc500 }} />Selecionar arquivo</DialogTitle>
          </DialogHeader>
          {filePickerLoading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" style={{ color: tc500 }} /></div>
          ) : filePickerList.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">Nenhum arquivo neste negócio.</p>
          ) : (
            <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
              {filePickerList.map(f => {
                const ext = f.original_filename?.split(".").pop()?.toUpperCase() || "?";
                const size = f.size > 1024 * 1024 ? `${(f.size / 1024 / 1024).toFixed(1)} MB` : `${(f.size / 1024).toFixed(0)} KB`;
                return (
                  <button key={f.id} onClick={() => {
                    setWinDocUploads(prev => ({ ...prev, [filePickerIdx]: { ...f, _existing: true, name: f.original_filename } }));
                    setFilePickerOpen(false);
                  }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors text-left">
                    <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-[9px] font-bold text-slate-500">{ext}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{f.original_filename}</p>
                      <p className="text-[10px] text-slate-400">{size} · {new Date(f.created_at).toLocaleDateString("pt-BR")}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
